'use strict';

/**
 * System Activity (Work Item 230). A tenant-scoped overview of how much work and knowledge the team
 * creates over time, read from the pre-aggregated ActivityAggregate table (GET /1.0/Activity/Aggregate
 * via providers.OpsAPI.loadActivityAggregate). This view owns the page shell and hosts the reusable
 * pict-section-activitygraph chart (registered separately as 'Plansheet-SystemActivity-Chart', pointed
 * at the #SystemActivity-Chart slot in this template). The chart's granularity switcher calls back into
 * loadPeriod() through the section's PeriodChangeHandler, so a new granularity refetches that period.
 */

const libPictView = require('pict-view');

class PictViewSystemActivity extends libPictView
{
	_chart() { return this.pict.views['Plansheet-SystemActivity-Chart']; }
	_api() { return this.pict.providers.OpsAPI; }
	_store() { return this.pict.AppData.SystemActivity || {}; }

	show(pPeriod)
	{
		this.render();
		let tmpChart = this._chart();
		if (tmpChart) { tmpChart.render(); }
		let tmpPeriod = pPeriod || (tmpChart && tmpChart._State && tmpChart._State.Period) || 'Week';
		// loadPeriod, not selectPeriod: this is what the route handler already called.
		return this.loadPeriod(tmpPeriod);
	}

	/**
	 * The user switched granularity. NAVIGATES; the route handler renders.
	 *
	 * Day, Week, Month, Quarter and Year each refetch their own buckets and draw a visibly different
	 * chart, so which one you are looking at is worth a URL. It could not be linked or reloaded into.
	 */
	selectPeriod(pPeriod)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSystemActivityPeriod === 'function'
			&& tmpApp.showSystemActivityPeriod(pPeriod)) { return; }
		return this.loadPeriod(pPeriod);
	}

	// Fetch one granularity's buckets and hand them to the chart. Called on show and by the chart's
	// PeriodChangeHandler when the user switches granularity.
	loadPeriod(pPeriod)
	{
		let tmpAPI = this._api();
		let tmpChart = this._chart();
		if (!tmpAPI || typeof tmpAPI.loadActivityAggregate !== 'function' || !tmpChart) { return Promise.resolve(); }
		return tmpAPI.loadActivityAggregate({ period: pPeriod }).then((pBody) =>
		{
			let tmpRows = (pBody && pBody.Rows) || this._store().Rows || [];
			tmpChart.setData({ Period: pPeriod, Buckets: tmpRows });
			this._reflectError();
			return pBody;
		});
	}

	refresh() { return this.loadPeriod((this._chart() && this._chart()._State && this._chart()._State.Period) || 'Week'); }

	_reflectError()
	{
		let tmpError = this._store().LoadError || '';
		this.pict.ContentAssignment.assignContent('#SystemActivity-Error', tmpError ? ('Could not load activity: ' + tmpError) : '');
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewSystemActivity;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-SystemActivity',
	DefaultRenderable: 'Plansheet-SystemActivity',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-sysact { padding: 20px 24px 60px; max-width: 1040px; }
		.ps-sysact-head { display: flex; align-items: center; gap: 14px; margin-bottom: 4px; }
		.ps-sysact-head h1 { font-size: 22px; font-weight: 600; margin: 0; flex: 1; color: var(--theme-color-text-primary, #2c3140); }
		.ps-sysact-refresh { padding: 7px 14px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-sysact-refresh:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-sysact-sub { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 18px; }
		.ps-sysact-err:not(:empty) { background: var(--theme-color-status-error, #cc3333); color: var(--theme-color-text-on-brand, #fff); padding: 8px 12px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; }
		.ps-sysact-chart { min-height: 200px; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-SystemActivity',
			Template: /*html*/`
<div class="ps-sysact">
	<div class="ps-sysact-head">
		<h1>System Activity</h1>
		<button class="ps-sysact-refresh" onclick="_Pict.views['Plansheet-SystemActivity'].refresh()">Refresh</button>
	</div>
	<p class="ps-sysact-sub">How much work and knowledge the team creates over time. One bar per period, stacked by type or actor.</p>
	<div class="ps-sysact-err" id="SystemActivity-Error"></div>
	<div class="ps-sysact-chart" id="SystemActivity-Chart"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-SystemActivity',
			TemplateHash: 'Plansheet-SystemActivity',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
